import React, { useState, useEffect } from 'react';
import { Progress } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { isMobileOnly } from 'react-device-detect';

type Props = {
	env: any;
	steps: any;
	stage: any;
	platform: string;
};

export type ReduxStore = {
	auth: StoreAuth;
};

export type StoreAuth = {
	token: string;
	roles: string;
	operations: any;
};

const PLATFORM_STYLES: any = {
	gibobs: {
		primary: '#02C3CD',
		incomplete: '#748EA0',
		complete: '#52C41A',
		trailColor: '#C0F0F3',
		strokeWidth: 4
	},
	sabadell: {
		primary: '#004799',
		incomplete: '#004799',
		complete: '#52C41A',
		trailColor: 'white',
		strokeWidth: 4
	},
	engel: {
		primary: '#C80000',
		incomplete: '#C80000',
		complete: '#05D36D',
		trailColor: '#808080',
		strokeWidth: 2
	},
	safecredit: {
		primary: '#002087',
		incomplete: '#002087',
		complete: '#05D36D',
		trailColor: '#808998',
		strokeWidth: 2
	}
};

const ProgressBySteps = ({ steps, stage, env, platform }: Props) => {
	const { t } = useTranslation();
	const userToken = useSelector<ReduxStore, string>((state) => state.auth.token);
	const [colorNif, setColorNif] = useState(
		PLATFORM_STYLES[platform]?.incomplete || PLATFORM_STYLES.default.incomplete
	);
	const [colorOfficialDocuments, setColorOfficialDocuments] = useState(
		PLATFORM_STYLES[platform]?.incomplete || PLATFORM_STYLES.default.incomplete
	);
	const { operationId, owner } = useParams<any>();

	useEffect(() => {
		getDocuments();
	}, [stage]);

	useEffect(() => {
		const platformColor = PLATFORM_STYLES[platform] || PLATFORM_STYLES.default;
		setColorNif(platformColor.incomplete);
		setColorOfficialDocuments(platformColor.incomplete);
	}, [owner]);

	const getDocuments = async () => {
		const response = await axios.get(
			`${env.api.url}/v1/documents/get-operation-documents/${operationId}`,
			{
				headers: { Authorization: `Bearer ${userToken}` }
			}
		);
		let nifData: any = [];
		nifData = response.data.data.filter((item: any) => item.type === 'nif' && item.owner === owner);
		if (nifData.length !== 0) {
			const platformColor = PLATFORM_STYLES[platform] || PLATFORM_STYLES.default;
			setColorNif(platformColor.complete);
		}
		let officialDocuments = [];
		officialDocuments = response.data.data.filter(
			(item: any) =>
				(item.type === 'laboralLife' ||
					item.type === 'contributionBase' ||
					item.type === 'paymentsAEAT' ||
					item.type === 'tgss') &&
				item.owner === owner
		);
		if (officialDocuments.length !== 0) {
			const platformColor = PLATFORM_STYLES[platform] || PLATFORM_STYLES.default;
			setColorNif(platformColor.complete);
		}
	};

	const nameStages: any = {
		1:
			platform === 'engel' || platform === 'safecredit' ? (
				<strong>
					{t('form.dni')} {t(`mydocuments.${owner}`)}
				</strong>
			) : (
				t('doc.differentStagesAutomatic')
			),
		2:
			platform === 'engel' || platform === 'safecredit' ? (
				<strong>
					{t('doc.official')} {t(`mydocuments.${owner}`)}
				</strong>
			) : (
				t('doc.differentStages2Automatic')
			),
		3: t('doc.differentStages3Automatic')
	};

	const nameStagesSabadell: any = {
		1: t('stage.newDocSabadell'),
		2: t('form.dni'),
		3: t('download.afterBankSabdell'),
		// 3: t('download.afterBankSabdell'),
		4: t('doc.differentStages3Automatic')
	};

	return (
		<div
			className="Progress-by-steps"
			style={{
				marginTop:
					!isMobileOnly && platform === 'gibobs'
						? -35
						: isMobileOnly && (platform === 'engel' || platform === 'safecredit')
						? -33
						: undefined
			}}>
			<div className="steps-and-title">
				<div style={{ display: isMobileOnly ? undefined : 'flex', alignItems: 'center' }}>
					<h1 className="steps-and-title-text" style={{ fontSize: isMobileOnly ? 14 : 16 }}>
						{t('doc.stagesDocumentation')} {stage}:
					</h1>
					<h1
						className="steps-and-title-text"
						style={{
							fontSize: isMobileOnly ? 14 : 16,
							marginLeft: 4
						}}>
						{platform === 'sabadell' ? nameStagesSabadell[stage] : nameStages[stage]}
					</h1>
				</div>
				<Progress
					className={'steps-mobile'}
					percent={(stage * 100) / steps}
					steps={steps}
					showInfo={false}
					strokeColor={[
						stage !== 1 ? colorNif : PLATFORM_STYLES[platform]?.primary,
						stage !== 2 ? colorOfficialDocuments : PLATFORM_STYLES[platform]?.primary,
						PLATFORM_STYLES[platform]?.primary
					]}
					trailColor={PLATFORM_STYLES[platform]?.trailColor}
					strokeWidth={PLATFORM_STYLES[platform]?.strokeWidth}
					aria-label={`Progreso del formulario: ${Math.round((stage * 100) / steps)}% completado`}
				/>
			</div>
		</div>
	);
};

export default ProgressBySteps;
